import React from 'react';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, Shield, Zap } from 'lucide-react';

const SolutionMinimal = () => {
  const capabilities = [
    {
      icon: Brain,
      title: 'Inteligencia Predictiva',
      description: 'Modelos de IA que anticipan tendencias de mercado, comportamiento de clientes y riesgos operacionales.',
    },
    {
      icon: TrendingUp,
      title: 'Optimización de Decisiones',
      description: 'Dashboards ejecutivos que transforman datos complejos en insights accionables para la alta gerencia.',
    },
    {
      icon: Shield,
      title: 'Gestión de Riesgos',
      description: 'Detección temprana de amenazas financieras, operativas y de mercado antes de que impacten tu negocio.',
    },
    {
      icon: Zap,
      title: 'Automatización Inteligente',
      description: 'Procesos de análisis que antes tomaban semanas, ahora en tiempo real con precisión superior al 90%.',
    },
  ];

  return (
    <section id="solution" className="py-24 bg-slate-950">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white mb-4"
            data-cms="solution.title"
          >
            Cómo transformamos tu negocio
          </h2>
          <p 
            className="text-slate-400 max-w-2xl mx-auto"
            data-cms="solution.subtitle"
          >
            Arquitectura de inteligencia empresarial diseñada para 
            ejecutivos que necesitan certeza en sus decisiones.
          </p>
        </motion.div>

        {/* Capabilities Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {capabilities.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
              className="group relative p-8 rounded-2xl bg-white/[0.02] border border-white/[0.05] hover:bg-white/[0.04] hover:border-blue-500/30 transition-all duration-300 cursor-pointer overflow-hidden"
            >
              {/* Hover glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-cyan-500/0 group-hover:from-blue-500/5 group-hover:to-cyan-500/5 transition-all duration-500" />
              
              <div className="relative flex items-start gap-5">
                <motion.div 
                  className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center shrink-0 group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-all duration-300"
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.5 }}
                >
                  <item.icon className="w-6 h-6 text-blue-400 group-hover:text-blue-300 transition-colors" />
                </motion.div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-100 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed group-hover:text-slate-300 transition-colors">
                    {item.description}
                  </p>
                </div>
              </div>
              
              {/* Bottom line animation */}
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-blue-500 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </motion.div>
          ))}
        </div>

        {/* Bottom Stats - Minimal */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 pt-16 border-t border-white/[0.05]"
        >
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-white mb-1">18</div>
              <div className="text-sm text-slate-500">Proyectos Ejecutados</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">5</div>
              <div className="text-sm text-slate-500">Países Cobertura</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-1">5</div>
              <div className="text-sm text-slate-500">Años en Sistemas IA</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionMinimal;
